import React, { useEffect, useState } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import { AllergenFallback } from 'components/icons/AllergenFallback';

import { LoadingSpinner } from '../../../../../../components/layouts/LoadingSpinner';
import { NutritionMenuItemLabel } from '../../../../../../core/api/menu/menu.types';
import { DynamicSvgIcon } from '../DynamicSvgIcon';

const useStyles = makeStyles((theme: Theme) => ({
  fallbackSvg: {
    height: 64,
    width: 64,
  },
  loadingSpinner: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  svgIcon: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
}));

type SvgString = {
  name: string;
  icon: string | JSX.Element;
};

type Props = {
  allergens: NutritionMenuItemLabel[];
};

export function AllergenIcons(props: Props): JSX.Element {
  const { allergens } = props;
  const classes = useStyles();

  const [svgs, setSvgs] = useState<SvgString[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (allergens.length) {
      const fetchSvgs = async (): Promise<SvgString[]> => {
        const svgResponses: SvgString[] = [];

        // eslint-disable-next-line no-restricted-syntax
        for (const allergen of allergens) {
          try {
            /* eslint-disable no-await-in-loop */
            const svgResponse = await axios.get(allergen.IconUrl, {
              withCredentials: false,
            });

            svgResponses.push({
              name: allergen.Name,
              icon: svgResponse.data,
            });
          } catch {
            svgResponses.push({
              name: allergen.Name,
              icon: (
                <AllergenFallback
                  data-fd="allergen-fallback-svg"
                  className={classes.fallbackSvg}
                />
              ),
            });
          }
        }

        return svgResponses;
      };

      fetchSvgs()
        .then((res) => {
          setSvgs(res);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [allergens]);

  if (isLoading) {
    return (
      <div className={classes.loadingSpinner}>
        <LoadingSpinner variant="panel" />
      </div>
    );
  }

  return (
    <>
      {svgs.map((svg) => (
        <div className={classes.svgIcon} key={svg.name}>
          <DynamicSvgIcon label={svg.name} svg={svg.icon} />
        </div>
      ))}
    </>
  );
}
